import React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import * as Routes from '@/modules/routes';
import SPAButton from '../../shared/SPAButton';
import PostLink from './PostLink';
import SectionActions from './SectionActions';
import { LessonSectionRow } from './useSections';

const columnHelper = createColumnHelper<LessonSectionRow>();

const linkClass = 'tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover tw-no-underline tw-transition-colors';

// Enrichment sections are state-scoped; every other section is visible to all.
const ViewCell = ({ section }: { section: LessonSectionRow }) => {
  const navigate = useNavigate();

  if (!section.enrichment) return <>All</>;

  return (
    <SPAButton
      aria-label={`Edit state-specific access for ${section.full_title}`}
      className="tw-whitespace-nowrap"
      size="xs"
      onClick={() => navigate(`${section.id}/states`)}
    >
      {/* No globe/world SVG in ui/Icon, so this keeps the legacy FA glyph. */}
      <i aria-hidden="true" className="fa fa-globe" />
      {section.states_selected}
    </SPAButton>
  );
};

// The legacy table's columns (admin/sections/_section). Nothing sorts: the order is the drag
// order. A missing tutorial is its Create action, a POST that lands on the new activity's legacy
// page; a reading check without questions is Create too, the GET that creates or opens the game.
const getSectionColumns = (programId: number, lessonId: number): ColumnDef<LessonSectionRow, unknown>[] => {
  const scoped = { lesson_id: lessonId, program_id: programId };

  return [
    columnHelper.accessor('full_title', {
      cell: info => (
        <a className={linkClass} href={Routes.edit_admin_section_path(info.row.original.id, scoped)}>
          {info.getValue()}
        </a>
      ),
      enableSorting: false,
      header: 'Section Title',
      meta: { width: 'auto' },
    }),
    columnHelper.display({
      cell: info => <ViewCell section={info.row.original} />,
      enableSorting: false,
      header: 'State View',
      id: 'view',
      meta: { align: 'center' },
      size: 120,
    }),
    columnHelper.display({
      cell: info => (
        <a className={linkClass} href={Routes.edit_admin_section_path(info.row.original.id, scoped)}>Edit</a>
      ),
      enableSorting: false,
      header: 'Text',
      id: 'text',
      meta: { align: 'center' },
      size: 70,
    }),
    columnHelper.accessor('tutorial_id', {
      cell: (info) => {
        const href = Routes.admin_section_activity_path(info.row.original.id, scoped);

        return info.getValue()
          ? <a className={linkClass} href={href}>Edit</a>
          : <PostLink className={linkClass} href={href}>Create</PostLink>;
      },
      enableSorting: false,
      header: 'Tutorial',
      meta: { align: 'center' },
      size: 90,
    }),
    columnHelper.accessor('reading_game_id', {
      cell: (info) => {
        const { id, reading_check_questions_count: questions } = info.row.original;
        const gameId = info.getValue();

        return gameId && questions
          ? (
            <a
              className={linkClass}
              href={Routes.edit_admin_reading_game_path(gameId, { ...scoped, section_id: id })}
            >
              {questions}
            </a>
          )
          : (
            <a className={linkClass} href={Routes.create_or_redirect_admin_section_reading_games_path(id, scoped)}>
              Create
            </a>
          );
      },
      enableSorting: false,
      header: 'Reading Check',
      meta: { align: 'center' },
      size: 120,
    }),
    columnHelper.accessor('notebooks_count', {
      cell: (info) => {
        const count = info.getValue();

        return (
          <a className={linkClass} href={Routes.admin_section_notebooks_path(info.row.original.id, scoped)}>
            {count} {count === 1 ? 'Notebook' : 'Notebooks'}
          </a>
        );
      },
      enableSorting: false,
      header: 'Notebook',
      meta: { align: 'center' },
      size: 120,
    }),
    columnHelper.display({
      cell: info => <SectionActions lessonId={lessonId} programId={programId} section={info.row.original} />,
      enableSorting: false,
      header: 'Actions',
      id: 'actions',
      meta: { align: 'center' },
      size: 110,
    }),
  ];
};

export default getSectionColumns;
